<template>
  <div class="nebSelect"
    v-neb-click-outside="onClickOutside"
  >
    <div class="nebSelect-select"
      @click="toggleShowDropdown"
      :class="{
        placeholder: !valueSelected,
        disabled,
        readOnly,
        open: showDropdown,
        dirty: highlightDirty && defaultValue !== undefined && value !== defaultValue
      }"
    >
      <span>{{ valueLabel }}</span>

      <neb-icon name="caretDown"
        height="12px"
        width="12px"
        v-show="clearable ? !readOnly && (!valueSelected || value === defaultValue) : !readOnly"
      ></neb-icon>

      <neb-icon name="x"
        height="12px"
        width="12px"
        v-show="clearable && !readOnly && !disabled && valueSelected && value !== defaultValue"
        @click.native="onClear"
      ></neb-icon>
    </div>

    <div class="nebSelect-dropdown"
      v-show="showDropdown"
    >
      <div class="nebSelect-search"
        v-if="searchable"
      >
        <neb-input v-model="formData.keyword"
          icon="search"
          placeholder="Search Entity"
        ></neb-input>
      </div>

      <div class="nebSelect-dropdown-option"
        v-for="(opt, idx) in filteredOptions"
        :key="idx"
        @click="select(opt)"
      >
        <span class="optionLabel">{{ opt.label }}</span>
      </div>

      <div class="nebSelect-dropdown-noOptions"
        v-show="filteredOptions.length < 1"
      >
        <neb-icon name="x"
          height="18px"
          width="18px"
        ></neb-icon>

        <div class="nebSelect-dropdown-noOptions-text">
          {{ emptyText !== '' ? emptyText : 'No Options' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { includes, lowerCase } from 'lodash'

import '../../directives/clickOutside'

import NebIcon from '../../Icon'
import NebInput from '../Input'

export default {
  name: 'nebSelect',

  components: {
    NebIcon,
    NebInput
  },

  props: {
    value: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Select Value'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    prefix: {
      type: String,
      default: ''
    },
    highlightDirty: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: String
    },
    clearable: {
      type: Boolean,
      default: false
    },
    /**
     * Specify text to be displayed when the select has no options.
     */
    emptyText: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    showDropdown: false,
    formData: {
      keyword: ''
    }
  }),

  computed: {
    valueLabel () {
      const option = this.options
        .find(opt => opt.value === this.value)

      const label = option ? option.label : this.placeholder

      return this.prefix ? `${this.prefix}: ${label}` : label
    },
    valueSelected () {
      return !!this.options
        .find(opt => opt.value === this.value)
    },
    filteredOptions () {
      if (!this.searchable) {
        return this.options
      } else {
        return this.options
          .filter(opt => includes(lowerCase(opt.label), lowerCase(this.formData.keyword)))
      }
    }
  },

  methods: {
    onClear (e) {
      e.stopPropagation()
      e.preventDefault()

      this.$emit('clear')
    },
    select (opt) {
      this.$emit('input', opt.value)
      this.showDropdown = false
    },
    toggleShowDropdown () {
      if (this.disabled || this.readOnly) { return }
      this.showDropdown = !this.showDropdown
    },
    onClickOutside () {
      if (this.showDropdown) {
        this.showDropdown = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../style.scss';

.nebSelect {
  height: 34px;
  position: relative;
  width: 100%;

  &-search {
    padding: 10px;
    box-sizing: border-box;
  }

  &-select {
    height: 34px;
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $light-grey-blue;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-left: 15px;
    padding-right: 15px;
    color: $slate-grey;
    font-family: $defaultFont;
    font-size: 12px;
    cursor: pointer;
    position: relative;

    span {
      margin-right: 10px;
      white-space: nowrap;
      overflow: hidden;
    }

    .nebIcon {
      color: $slate-grey;
      opacity: 0.7;
    }

    &.placeholder {
      color: $slate-grey;
    }

    &.disabled {
      color: $pinkish-grey;
      background-color: $white-four;
      cursor: not-allowed;

      .nebIcon {
        color: $warm-grey;
      }
    }

    &.readOnly {
      border-top: none;
      border-left: none;
      border-right: none;
      border-bottom: 2px solid $warm-grey;
      cursor: initial;
      border-radius: initial;
    }

    &.open {
      box-shadow: 0 0 3px 0 $tiffany-blue;
      border: solid 1px $tiffany-blue;
    }

    &.dirty {
      background-color: #e8f4fc;
      border: 1px solid #a0c3d2;
    }
  }

  &-dropdown {
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $pinkish-grey;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 39px;
    left: 0;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    box-sizing: border-box;
    max-height: 310px;
    overflow-x: hidden;
    overflow-y: scroll;
    z-index: 2001;

    &-noOptions {
      box-sizing: border-box;
      overflow: hidden;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 9px 15px;

      .nebIcon {
        color: $warm-grey;
        margin-right: 5px;
      }

      &-text {
        font-size: 12px;
        font-family: $defaultFont;
        color: $warm-grey;
      }
    }

    &-option {
      cursor: pointer;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      box-sizing: border-box;
      padding: 9px 15px;

      .optionLabel {
        font-size: 12px;
        font-family: $defaultFont;
        color: $slate-grey;
        white-space: nowrap;
      }

      &:hover {
        background-color: $duck-egg-blue;
      }
    }
  }
}
</style>
